import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { fetchScheduleRouteOptions, type ScheduleRouteOption } from "../data/routes";
import { fetchScheduleCoverageRange, type ScheduleCoverageRow } from "../data/schedule";
import { weekDays } from "../data/schedule-builder";
import {
  fetchSchedulePublishReview,
  publishShiftTemplateWeek,
  type SchedulePublishReceipt,
  type SchedulePublishReview as SchedulePublishReviewFacts,
} from "../data/schedule-publish-review";
import {
  createShiftTemplate,
  EMPTY_TEMPLATE_COLOR,
  EMPTY_TEMPLATE_OPEN_FILL_MODE,
  fetchShiftTemplates,
  SAFE_TEMPLATE_WRITE_FAILURE,
  updateShiftTemplate,
  type ShiftTemplateDraft,
  type ShiftTemplateRow,
} from "../data/schedule-templates";
import {
  buildPublishReceiptLine,
  resolveSchedulePublishGate,
} from "../lib/schedule-publish-gate";
import {
  buildReviewPreview,
  buildWeekOptions,
  builderWeekNote,
  daysSummary,
  EMPTY_PUBLISH_PREVIEW,
  formatMonthDay,
  isBuilderWeekStart,
  previewFromHostExpansion,
  PUBLISH_BLOCK_REASONS,
  resolvePublishBlock,
  shiftHours,
  type PublishPreview,
} from "../lib/schedule-wizard-model";
import { markDraftSavedThisSession } from "../lib/schedule-wizard-session";
import { DEFAULT_SHIFT_DURATION_MINUTES } from "../lib/shift-duration";
import { DEFAULT_SITE_TIMEZONE } from "../lib/timezone-options";
import { ScheduleWizard, type ScheduleWizardStepKey } from "./ScheduleWizard";
import { TemplateDraftFields } from "./TemplateDraftFields";

// Console Schedule Wave 2 Slice C: the connected create-and-publish flow. Slice A shipped
// the chrome (ScheduleWizard), Slice B the presentation model; this surface wires both to
// the EXISTING hosts and nothing else: create_shift_template / update_shift_template for
// the Define step, the schedule_coverage read for the honest Review preview, and
// publish_shift_template for Publish. Zero new RPCs, zero new destructive semantics.
//
// Board Implement rule 1 says the Define step autosaves on change. This surface writes on
// Continue and on Save draft instead, deliberately: the create/update hosts answer partial
// drafts with their own validation sentences ("Template name is required."), so a
// per-keystroke autosave would surface a failure toast mid-typing on every field the
// operator had not reached yet. Continue and Save draft are the two ways to leave Define
// forward or sideways, and both write, so leaving forward or sideways never loses work.
// Cancel is the explicit abandon and says nothing else.
//
// COUNT RULING (Slice B, not reopened): the Review headline and the proof chip use the
// host's created-slot count, one slot per date carrying headcount as a column, never
// nights times officers. Review computes it through `previewFromHostExpansion`, which
// mirrors the shipped RPC's (date, starts_at) idempotency match; the proof chip repeats
// the server's own receipt.

/** The steps a URL may name. `published` is a result, never an address. */
export type ScheduleWizardRouteStep = Exclude<ScheduleWizardStepKey, "published">;

function emptyDraft(): ShiftTemplateDraft {
  return {
    siteId: "",
    routeId: "",
    name: "",
    siteTimezone: DEFAULT_SITE_TIMEZONE,
    localStartTime: "",
    durationMinutes: DEFAULT_SHIFT_DURATION_MINUTES,
    weekdays: [],
    activeFrom: "",
    activeUntil: null,
    headcountRequired: 1,
    requiredCredentialTypes: [],
    colorToken: EMPTY_TEMPLATE_COLOR,
    openFillMode: EMPTY_TEMPLATE_OPEN_FILL_MODE,
  };
}

function draftFromRow(row: ShiftTemplateRow): ShiftTemplateDraft {
  return {
    siteId: row.siteId ?? "",
    routeId: row.routeId ?? "",
    name: row.name,
    siteTimezone: row.siteTimezone,
    localStartTime: row.localStartTime.slice(0, 5),
    durationMinutes: row.durationMinutes,
    weekdays: [...row.weekdays],
    activeFrom: row.activeFrom ?? "",
    activeUntil: row.activeUntil,
    headcountRequired: row.headcountRequired,
    requiredCredentialTypes: [...row.requiredCredentialTypes],
    colorToken: row.colorToken,
    openFillMode: row.openFillMode,
  };
}

/** Today's date where this browser sits. Injectable so tests pin the week rows. */
function localTodayIso(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Roving focus for the week radiogroup. Selection remains an explicit Space or Enter action. */
function nextWeekIndex(key: string, index: number, count: number): number | null {
  if (key === "ArrowRight" || key === "ArrowDown") return (index + 1) % count;
  if (key === "ArrowLeft" || key === "ArrowUp") return (index - 1 + count) % count;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return null;
}

const HYDRATE_MISSING =
  "This pattern is not in the publishable list. It may have been retired or discarded, or your access may have changed.";

/**
 * The one sentence a failed coverage read speaks (QA Q1): it is the block reason beside
 * the neutral control, with Try again as its repair. Nothing else on the surface repeats
 * or contradicts it, and no count is claimed for a week nobody could check.
 */
export const COVERAGE_UNREADABLE =
  "Could not check this week's existing shifts, so publish cannot preview honestly";

interface CoverageLoad {
  readonly weekStart: string;
  readonly rows: readonly ScheduleCoverageRow[];
}

export function ScheduleWizardFlow({
  calendarMode = false,
  client,
  accountId,
  canManage,
  step,
  templateId,
  addressedWeekStart,
  initialWeekStart,
  todayIso,
  onStepChange,
  onTemplateStored,
  onExit,
  onOpenBuilder,
}: {
  /** Names the Calendar destination while preserving legacy embedded Builder copy. */
  calendarMode?: boolean;
  client: SupabaseClient;
  accountId: string;
  /** schedule.manage. Display gate only; every host re-checks server-side. */
  canManage: boolean;
  /** The step the URL names (`/schedule/new?step=...`). */
  step: ScheduleWizardRouteStep;
  /** Resume target from `?template=`; null starts a fresh draft at Define. */
  templateId: string | null;
  /** Validated `?week=` value while Review owns the address. */
  addressedWeekStart: string | null;
  /** Account-bound calendar handoff, used only to seed the mounted flow. */
  initialWeekStart?: string | null;
  /** Injectable clock for tests. */
  todayIso?: string;
  /** Move the address to a step, carrying the stored pattern so refresh and Back resume. */
  onStepChange: (
    step: ScheduleWizardRouteStep,
    templateId: string | null,
    weekStart?: string | null,
  ) => void;
  /** A draft landed on the server; the caller reflects its id into the current address. */
  onTemplateStored: (templateId: string) => void;
  /** Cancel / Done: back to the Schedule hub. */
  onExit: () => void;
  /** Success destination uses the server receipt's week, never today's week. */
  onOpenBuilder: (weekStart: string) => void;
}) {
  const [draft, setDraft] = useState<ShiftTemplateDraft>(emptyDraft);
  const [storedId, setStoredId] = useState<string | null>(null);
  const [templateStatus, setTemplateStatus] = useState<string>("draft");
  // Resume hydration: pending until the row backing ?template= has been read.
  const [hydrating, setHydrating] = useState(templateId !== null);
  const [hydrateFailure, setHydrateFailure] = useState<string | null>(null);
  const [routes, setRoutes] = useState<readonly ScheduleRouteOption[] | null>(null);
  const [weekStart, setWeekStart] = useState<string | null>(() => {
    const initial = step === "review" ? addressedWeekStart : initialWeekStart;
    return isBuilderWeekStart(initial) ? initial : null;
  });
  const weekRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [weekFocusIndex, setWeekFocusIndex] = useState(0);
  const [coverage, setCoverage] = useState<CoverageLoad | null>(null);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [coverageNonce, setCoverageNonce] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveFailure, setSaveFailure] = useState<string | null>(null);
  // True when the shape on screen differs from what the hosts hold: before the first
  // write, and after any edit until Continue or Save draft writes it (QA Q2/Q3). Review
  // refuses to preview or publish an unwritten shape.
  const [shapeDirty, setShapeDirty] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishFailure, setPublishFailure] = useState<string | null>(null);
  const [published, setPublished] = useState<SchedulePublishReceipt | null>(null);
  // The host's publish review for the chosen week: the one gate both Schedule publish
  // surfaces read (lock O-4). Keyed by pattern and week, so a review can never describe a
  // different week than the one on screen.
  const [hostReview, setHostReview] = useState<
    { readonly key: string; readonly facts: SchedulePublishReviewFacts } | null
  >(null);
  const [hostReviewError, setHostReviewError] = useState<string | null>(null);
  const [hostReviewNonce, setHostReviewNonce] = useState(0);
  // The preview the operator last reviewed, kept for the success ghost. Never shown as
  // the count: the proof chip is the server's receipt alone.
  const [reviewedPreview, setReviewedPreview] = useState<PublishPreview>(EMPTY_PUBLISH_PREVIEW);

  const today = todayIso ?? localTodayIso();

  // Review is refreshable and history-aware: the validated address restores the chosen
  // week. Missing or invalid input stays null so the existing honest block remains true.
  useEffect(() => {
    if (step === "review") {
      setWeekStart(isBuilderWeekStart(addressedWeekStart) ? addressedWeekStart : null);
    }
  }, [step, addressedWeekStart]);

  useEffect(() => {
    let active = true;
    fetchScheduleRouteOptions(client, accountId)
      .then((options) => {
        if (active) setRoutes(options);
      })
      .catch(() => {
        // The Define select degrades to its empty option; the create host still refuses a
        // missing route with its own sentence, so nothing can be silently mis-saved.
        if (active) setRoutes([]);
      });
    return () => {
      active = false;
    };
  }, [client, accountId]);

  // Resume: hydrate the draft from the row ?template= names. The default read is the
  // delete/retire lock's picker read (draft + published, retired excluded), so a retired
  // pattern resolves to the honest missing state rather than a wizard that publish refuses.
  useEffect(() => {
    if (templateId === null || templateId === storedId) {
      setHydrating(false);
      return;
    }
    let active = true;
    setHydrating(true);
    setHydrateFailure(null);
    fetchShiftTemplates(client, accountId)
      .then((load) => {
        if (!active) return;
        const row = load.templates.find((candidate) => candidate.templateId === templateId);
        if (row === undefined) {
          setHydrateFailure(HYDRATE_MISSING);
        } else {
          setDraft(draftFromRow(row));
          setStoredId(row.templateId);
          setTemplateStatus(row.status);
          setShapeDirty(false);
        }
        setHydrating(false);
      })
      .catch(() => {
        if (!active) return;
        setHydrateFailure("Could not load this pattern. Check your connection and try again.");
        setHydrating(false);
      });
    return () => {
      active = false;
    };
  }, [client, accountId, templateId, storedId]);

  // Review needs the chosen week's coverage rows so the preview mirrors the RPC's own
  // idempotency check. Keyed by week; a failure renders and can be retried.
  useEffect(() => {
    if (step !== "review" || weekStart === null || published !== null) {
      return;
    }
    if (coverage !== null && coverage.weekStart === weekStart && coverageError === null) {
      return;
    }
    let active = true;
    setCoverageError(null);
    const days = weekDays(weekStart);
    fetchScheduleCoverageRange(client, accountId, days[0]!, days[6]!)
      .then((rows) => {
        if (active) setCoverage({ weekStart, rows });
      })
      .catch(() => {
        if (active) {
          setCoverage(null);
          setCoverageError(COVERAGE_UNREADABLE);
        }
      });
    return () => {
      active = false;
    };
    // coverageNonce re-runs this read after a failed attempt when the operator retries;
    // the settled coverage state itself stays out of the deps so a landed read is final.
  }, [client, accountId, step, weekStart, published, coverageNonce]);

  // The host publish review. Read only when it can mean something: on Review, for a written
  // pattern, on a chosen week, before a publish has landed. The key pins pattern and week
  // together so a stale answer for the previous week is never rendered as this week's.
  const reviewKey = storedId !== null && weekStart !== null ? `${storedId}:${weekStart}` : null;
  useEffect(() => {
    if (step !== "review" || reviewKey === null || published !== null || shapeDirty) {
      return;
    }
    if (hostReview !== null && hostReview.key === reviewKey && hostReviewError === null) {
      return;
    }
    let active = true;
    setHostReviewError(null);
    const [templateForReview, weekForReview] = reviewKey.split(":") as [string, string];
    void fetchSchedulePublishReview(client, accountId, templateForReview, weekForReview).then(
      (outcome) => {
        if (!active) return;
        if (outcome.ok) {
          setHostReview({ key: reviewKey, facts: outcome.review });
        } else {
          setHostReview(null);
          setHostReviewError(outcome.reason);
        }
      },
    );
    return () => {
      active = false;
    };
    // hostReviewNonce re-runs the read after a failed attempt or a publish conflict; the
    // settled review stays out of the deps so a landed read is final.
  }, [client, accountId, step, reviewKey, published, shapeDirty, hostReviewNonce]);

  const routesById = useMemo(
    () => new Map((routes ?? []).map((route) => [route.routeId, route])),
    [routes],
  );
  const route = draft.routeId !== "" ? routesById.get(draft.routeId) : undefined;

  /** The row shape the preview helpers read, from the wizard's current truth. */
  const previewTemplate = useMemo<ShiftTemplateRow>(
    () => ({
      templateId: storedId ?? "",
      name: draft.name,
      siteId: draft.siteId === "" ? null : draft.siteId,
      routeId: draft.routeId === "" ? null : draft.routeId,
      siteTimezone: draft.siteTimezone,
      localStartTime: draft.localStartTime,
      durationMinutes: draft.durationMinutes,
      weekdays: draft.weekdays,
      headcountRequired: draft.headcountRequired,
      status: templateStatus,
      activeFrom: draft.activeFrom === "" ? null : draft.activeFrom,
      activeUntil: draft.activeUntil,
      requiredCredentialTypes: draft.requiredCredentialTypes,
      colorToken: draft.colorToken,
      openFillMode: draft.openFillMode,
    }),
    [draft, storedId, templateStatus],
  );

  const coverageReady =
    weekStart !== null && coverage !== null && coverage.weekStart === weekStart;
  const preview = useMemo(
    () =>
      coverageReady
        ? previewFromHostExpansion(previewTemplate, coverage.rows, weekStart)
        : EMPTY_PUBLISH_PREVIEW,
    [coverageReady, previewTemplate, coverage, weekStart],
  );

  const weekOptions = useMemo(() => {
    const options = buildWeekOptions({ todayIso: today });
    if (weekStart === null || options.some((option) => option.weekStart === weekStart)) {
      return options;
    }
    // A calendar selection can sit outside the usual four weeks. Keep its selected row
    // visible without changing the current/next-week labels on the ordinary options.
    const selected = buildWeekOptions({ todayIso: weekStart, count: 1 })[0];
    return selected === undefined
      ? options
      : [
          ...options,
          {
            ...selected,
            title: `Week of ${formatMonthDay(weekStart)}`,
            note: null,
            isCurrent: false,
            isNext: false,
          },
        ].sort((left, right) => left.weekStart.localeCompare(right.weekStart));
  }, [today, weekStart]);
  const selectedWeek = weekOptions.find((option) => option.weekStart === weekStart) ?? null;
  const weekLabel =
    weekStart !== null ? (selectedWeek?.title ?? `Week of ${formatMonthDay(weekStart)}`) : null;

  useEffect(() => {
    const selectedIndex = weekOptions.findIndex((option) => option.weekStart === weekStart);
    setWeekFocusIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [weekOptions, weekStart]);

  // A published pattern's shape is read-only (the update host refuses non-drafts); the
  // wizard can still publish more weeks of it, which is the Schedule this pattern path.
  const defineEditable = canManage && templateStatus === "draft";

  async function writeDraft(): Promise<string | null> {
    if (saving) return null;
    setSaving(true);
    setSaveFailure(null);
    try {
      const outcome =
        storedId === null
          ? await createShiftTemplate(client, accountId, draft)
          : await updateShiftTemplate(client, accountId, storedId, draft);
      if (!outcome.ok) {
        setSaveFailure(outcome.reason);
        return null;
      }
      setStoredId(outcome.templateId);
      setShapeDirty(false);
      markDraftSavedThisSession(outcome.templateId);
      // A week reviewed against the old shape is stale the moment the shape changes.
      setCoverage(null);
      return outcome.templateId;
    } catch {
      setSaveFailure(SAFE_TEMPLATE_WRITE_FAILURE);
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function continueFromDefine(): Promise<void> {
    if (!defineEditable) {
      onStepChange("week", storedId, weekStart);
      return;
    }
    const id = await writeDraft();
    if (id !== null) {
      onTemplateStored(id);
      onStepChange("week", id, weekStart);
    }
  }

  async function saveDraftAndExit(): Promise<void> {
    if (step === "define" && defineEditable) {
      const id = await writeDraft();
      if (id === null) {
        return; // the failure sentence renders; leaving would discard the correction
      }
    }
    onExit();
  }

  /**
   * Publish the reviewed week, against the revision the review issued.
   *
   * The revision comes from the gate, so a publish can only ever act on inputs this surface
   * actually showed. When the host answers conflict, the week moved underneath the operator
   * and nothing was written: the honest next move is to re-read the review, so the stale one
   * is dropped and the read is retriggered rather than leaving a number nobody can trust.
   */
  async function publish(): Promise<void> {
    if (publishBusy || weekStart === null || storedId === null || gate.reviewRevision === null) return;
    setPublishBusy(true);
    setPublishFailure(null);
    try {
      const result = await publishShiftTemplateWeek(
        client,
        accountId,
        storedId,
        weekStart,
        gate.reviewRevision,
      );
      if (result.ok) {
        setReviewedPreview(preview);
        if (result.receipt.templateStatus !== null) {
          setTemplateStatus(result.receipt.templateStatus);
        }
        setPublished(result.receipt);
        return;
      }
      if (result.kind === "conflict") {
        setPublishFailure(result.reason);
        setHostReview(null);
        setHostReviewNonce((nonce) => nonce + 1);
        // The coverage rows behind the per-day table are equally stale now.
        setCoverage(null);
        setCoverageNonce((nonce) => nonce + 1);
        return;
      }
      if (result.kind === "refused") {
        // The host refused on a blocker. Re-read so the surface states that blocker itself
        // rather than keeping a review that claimed the publish could run.
        setHostReview(null);
        setHostReviewNonce((nonce) => nonce + 1);
        return;
      }
      setPublishFailure(result.reason);
    } finally {
      setPublishBusy(false);
    }
  }

  function scheduleAnotherWeek(): void {
    setPublished(null);
    setPublishFailure(null);
    setWeekStart(null);
    setCoverage(null);
    setHostReview(null);
    setHostReviewError(null);
    setReviewedPreview(EMPTY_PUBLISH_PREVIEW);
    onStepChange("week", storedId, null);
  }

  // ---- Early states that replace the wizard body ------------------------------------------

  if (hydrating) {
    return (
      <p className="surface__state" role="status">
        Loading this pattern...
      </p>
    );
  }
  if (hydrateFailure !== null) {
    return (
      <div className="schedwiz-flow__missing">
        <p className="surface__state surface__state--error" role="alert">
          {hydrateFailure}
        </p>
        <button type="button" className="builder__navbtn" onClick={onExit}>
          Back to Schedule
        </button>
      </div>
    );
  }

  // `published` is a result, never an address: the URL keeps saying review.
  const shownStep: ScheduleWizardStepKey = published !== null ? "published" : step;
  const patternLabel = draft.name.trim().length > 0 ? draft.name.trim() : null;
  const contextLabel =
    shownStep === "review" || shownStep === "published"
      ? [patternLabel, weekLabel].filter((part) => part !== null).join(" · ") || undefined
      : patternLabel !== null
        ? `${patternLabel} · ${templateStatus}`
        : undefined;

  const draftSaved = storedId !== null && !shapeDirty;
  const resolvedBlock =
    shownStep === "review"
      ? resolvePublishBlock({
          canManageSchedule: canManage,
          templateStatus,
          weekdayCount: draft.weekdays.length,
          weekStart,
          preview,
          draftSaved,
        })
      : null;
  // While the coverage read is still in flight the preview is EMPTY, so "nothing new"
  // would be a lie about a week nobody has checked yet (QA Q1). That one kind stays
  // silent until coverage lands; every other reason is true regardless and keeps
  // speaking. A failed read becomes its own honest block with retry as the repair.
  const coverageLoading = weekStart !== null && !coverageReady && coverageError === null;
  const localBlock =
    resolvedBlock !== null && resolvedBlock.kind === "nothing-new" && !coverageReady
      ? null
      : resolvedBlock;
  // The shared gate (lock O-4). The local model still speaks first for the things only this
  // surface knows: permission display, an unwritten shape, no days, and no week chosen. Once
  // those are clear, the HOST's review is the authority on what the week creates and on why
  // it cannot run, because it is the same review the publish act will re-check.
  const hostFacts = reviewKey !== null && hostReview?.key === reviewKey ? hostReview.facts : null;
  const gate = resolveSchedulePublishGate({
    canManageSchedule: canManage,
    review: hostFacts,
    reviewLoading: shownStep === "review" && reviewKey !== null && hostReviewError === null,
    reviewError: hostReviewError,
    draftSaved,
  });
  const block = shownStep !== "review" ? null : (localBlock ?? gate.block);
  const coverageBlocked =
    shownStep === "review" && block === null && weekStart !== null && coverageError !== null;
  // The preview card and ghost render only when they can be honest: the shape is written
  // and the chosen week's existing shifts have been read (the locked zero string stays,
  // because no-week is exactly what it is locked for).
  const previewTrustworthy = draftSaved && (weekStart === null || coverageReady);

  const reviewCopy =
    shownStep === "review" || shownStep === "published"
      ? buildReviewPreview({
          template: previewTemplate,
          weekStart,
          preview: published !== null ? reviewedPreview : preview,
          siteName: route?.siteName ?? null,
          routeName: route?.routeName ?? null,
        })
      : null;

  const hours = shiftHours(previewTemplate);
  const days = daysSummary({ weekdays: draft.weekdays, overnight: hours?.overnight ?? false });

  return (
    <ScheduleWizard
      step={shownStep}
      {...(contextLabel !== undefined ? { contextLabel } : {})}
      {...(shownStep === "define"
        ? { help: "Set the repeating pattern. You will pick the week next." }
        : shownStep === "week"
          ? {
              help: "Pick the week these shifts land on. One week at a time; schedule more weeks after publishing.",
            }
          : {})}
      onCancel={onExit}
      {...(shownStep === "define" || shownStep === "week"
        ? { onSaveDraft: () => void saveDraftAndExit() }
        : {})}
      {...(shownStep === "week" || shownStep === "review"
        ? {
            onBack: () =>
              onStepChange(shownStep === "week" ? "define" : "week", storedId, weekStart),
          }
        : {})}
      {...(shownStep === "define"
        ? {
            primary: {
              label: "Continue to week",
              onClick: () => void continueFromDefine(),
              busy: saving,
              ...(canManage ? {} : { blockedReason: PUBLISH_BLOCK_REASONS["no-permission"] }),
            },
          }
        : {})}
      {...(shownStep === "week"
        ? {
            primary: {
              label: "Continue to review",
              onClick: () => onStepChange("review", storedId, weekStart),
              ...(canManage
                ? weekStart === null
                  ? { blockedReason: PUBLISH_BLOCK_REASONS["no-week"] }
                  : {}
                : { blockedReason: PUBLISH_BLOCK_REASONS["no-permission"] }),
            },
          }
        : {})}
      {...(shownStep === "review"
        ? {
            primary: {
              label: "Publish for officers",
              onClick: () => void publish(),
              busy: publishBusy || coverageLoading,
              ...(block !== null
                ? { blockedReason: block.reason }
                : coverageBlocked
                  ? { blockedReason: COVERAGE_UNREADABLE }
                  : {}),
              ...(block !== null && block.repairLabel !== null && block.repairStep !== null
                ? {
                    repair: {
                      label: block.repairLabel,
                      onClick: () =>
                        onStepChange(
                          block.repairStep as ScheduleWizardRouteStep,
                          storedId,
                          weekStart,
                        ),
                    },
                  }
                : coverageBlocked
                  ? {
                      repair: {
                        label: "Try again",
                        onClick: () => setCoverageNonce((nonce) => nonce + 1),
                      },
                    }
                  : {}),
            },
          }
        : {})}
      {...(shownStep === "published"
        ? { footNote: "This screen is reachable again from the pattern row: Schedule this pattern." }
        : {})}
      sideRail={
        shownStep === "define" ? (
          <DefineSideRail
            patternLabel={patternLabel}
            route={route}
            days={days}
            hours={hours !== null ? `${hours.label}${hours.overnight ? ", overnight" : ""}` : null}
            headcount={draft.headcountRequired}
          />
        ) : shownStep === "week" ? (
          <CarryForwardRail
            patternLabel={patternLabel}
            route={route}
            days={days}
            hours={hours?.label ?? null}
            headcount={draft.headcountRequired}
          />
        ) : shownStep === "review" ? (
          <ReviewSideRail preview={preview} coverageReady={coverageReady} daysSummaryLabel={days} />
        ) : (
          <PublishedSideRail openFillMode={draft.openFillMode} calendarMode={calendarMode} />
        )
      }
    >
      {shownStep === "define" ? (
        <div className="schedwiz-flow__fields">
          {defineEditable ? (
            <TemplateDraftFields
              draft={draft}
              routes={routes ?? []}
              busy={saving}
              onPatch={(partial) => {
                setDraft((current) => ({ ...current, ...partial }));
                setShapeDirty(true);
              }}
            />
          ) : (
            <p className="surface__state" role="status">
              {canManage
                ? "This pattern is published, so its shape is locked. Continue to pick another week to publish."
                : "Viewing only. Schedule manage permission is required to define or publish shifts."}
            </p>
          )}
          {saveFailure !== null ? (
            <p className="surface__state surface__state--error" role="alert">
              {saveFailure}
            </p>
          ) : null}
        </div>
      ) : null}

      {shownStep === "week" ? (
        <div className="schedwiz-flow__weeks" role="radiogroup" aria-label="Publish week">
          {weekOptions.map((option, index) => {
            const selected = option.weekStart === weekStart;
            const note = selected ? builderWeekNote(option.weekStart) : null;
            const selectWeek = () => {
              setWeekFocusIndex(index);
              setWeekStart(option.weekStart);
              setPublishFailure(null);
            };
            return (
              <button
                key={option.weekStart}
                ref={(node) => {
                  weekRefs.current[index] = node;
                }}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={index === weekFocusIndex ? 0 : -1}
                className={selected ? "wkrow wkrow--selected" : "wkrow"}
                onClick={selectWeek}
                onKeyDown={(event) => {
                  const target = nextWeekIndex(event.key, index, weekOptions.length);
                  if (target !== null) {
                    event.preventDefault();
                    setWeekFocusIndex(target);
                    weekRefs.current[target]?.focus();
                    return;
                  }
                  if (event.key === " " || event.key === "Enter") {
                    event.preventDefault();
                    selectWeek();
                  }
                }}
              >
                <span className="wkrow__body">
                  <span className="wkrow__title">{option.title}</span>
                  <span className="wkrow__range">
                    {option.rangeLabel}
                    {option.note !== null ? ` · ${option.note}` : ""}
                    {note !== null ? ` · ${note.charAt(0).toLowerCase()}${note.slice(1)}` : ""}
                  </span>
                </span>
                {option.isNext ? <span className="schedule__chip schedule__chip--draft wkrow__chip">Next week</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {shownStep === "review" ? (
        <div className="schedwiz-flow__review">
          {/* The card renders only when it can be honest: shape written, and the chosen
              week's existing shifts read. The block reason beside the control speaks for
              every other state, so no second sentence contradicts it (QA Q1/Q2). */}
          {previewTrustworthy && reviewCopy !== null ? (
            <div className="wizpreview">
              {/* The claim is the HOST's count once its review has landed (lock O-4). The
                  client expansion still draws the per-day ghost below, but it never gets to
                  state the number the operator is about to act on: one week, one count,
                  from the same review the publish act re-checks. Services and physical
                  obligation seats are stated separately and never multiplied together. */}
              <p className="wizpreview__line">
                {hostFacts !== null ? gate.countLine : reviewCopy.headline}
              </p>
              {gate.consequenceLine !== null ? (
                <p className="wizpreview__line wizpreview__consequence">{gate.consequenceLine}</p>
              ) : null}
              <ul className="wizpreview__facts">
                {reviewCopy.facts.map((fact) => (
                  <li key={fact}>{fact}</li>
                ))}
                {gate.warningLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {coverageLoading ? (
            <p className="surface__state" role="status">
              Checking this week's existing shifts...
            </p>
          ) : null}
          {previewTrustworthy && weekStart !== null ? (
            <>
              <p className="wizghost__label">The week, previewed</p>
              <WeekGhost
                weekStart={weekStart}
                preview={preview}
                startClock={draft.localStartTime.slice(0, 5)}
                headcount={draft.headcountRequired}
                live={false}
              />
            </>
          ) : null}
          {previewTrustworthy ? (
            <p className="wizghost__footnote">{reviewCopy?.footnote}</p>
          ) : null}
          {publishFailure !== null ? (
            <p className="surface__state surface__state--error" role="alert">
              {publishFailure}
            </p>
          ) : null}
        </div>
      ) : null}

      {shownStep === "published" && published !== null ? (
        <div className="schedwiz-flow__proof">
          {/* Board rule 8: no optimistic green. The chip repeats the server's own count. */}
          <p className="schedwiz-proof" role="status">
            {buildPublishReceiptLine(published)}
          </p>
          <p className="schedwiz-proof__audit">
            {weekLabel ?? "This week"} · the publish audit line is recorded server-side
            {published.weekSlotCount > published.createdSlotCount
              ? ` · ${published.weekSlotCount - published.createdSlotCount} ${
                  published.weekSlotCount - published.createdSlotCount === 1
                    ? "shift was"
                    : "shifts were"
                } already on this week and stay as they are`
              : ""}
          </p>
          {weekStart !== null && reviewedPreview.newSlotCount === published.createdSlotCount ? (
            <>
              <p className="wizghost__label">On the calendar now</p>
              <WeekGhost
                weekStart={weekStart}
                preview={reviewedPreview}
                startClock={draft.localStartTime.slice(0, 5)}
                headcount={draft.headcountRequired}
                live
              />
            </>
          ) : null}
          <div className="schedwiz-flow__next">
            <button type="button" className="schedwiz__primary" onClick={() => onOpenBuilder(published.weekStart)}>
              {calendarMode ? "View in Calendar" : "View in Builder"}
            </button>
            <button type="button" className="schedwiz__ghost" onClick={scheduleAnotherWeek}>
              Schedule another week
            </button>
            <button type="button" className="schedwiz__quiet" onClick={onExit}>
              Done
            </button>
          </div>
        </div>
      ) : null}
    </ScheduleWizard>
  );
}

/** Seven dashed day columns; new slots drawn in blue outline, proof green once live. */
function WeekGhost({
  weekStart,
  preview,
  startClock,
  headcount,
  live,
}: {
  weekStart: string;
  preview: PublishPreview;
  startClock: string;
  headcount: number;
  live: boolean;
}) {
  const newDates = new Set(preview.newDates);
  const carriedDates = new Set(preview.alreadyPublishedDates);
  return (
    <div className="wizghost" aria-hidden={false}>
      {weekDays(weekStart).map((date) => (
        <div key={date} className="wizghost__col">
          <p className="wizghost__day">{`${formatMonthDay(date)}`}</p>
          {newDates.has(date) ? (
            <span className={live ? "wizghost__slot wizghost__slot--live" : "wizghost__slot"}>
              {startClock !== "" ? `${startClock} x${headcount}` : `x${headcount}`}
              {live ? " · open" : ""}
            </span>
          ) : carriedDates.has(date) ? (
            <span className="wizghost__slot wizghost__slot--carried">already there</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function RailFact({ children }: { children: ReactNode }) {
  return <li className="wizrail__fact">{children}</li>;
}

function DefineSideRail({
  patternLabel,
  route,
  days,
  hours,
  headcount,
}: {
  patternLabel: string | null;
  route: ScheduleRouteOption | undefined;
  days: string | null;
  hours: string | null;
  headcount: number;
}) {
  return (
    <div className="wizrail">
      <p className="wizrail__label">So far</p>
      <ul className="wizrail__facts">
        {patternLabel !== null ? <RailFact>{patternLabel}</RailFact> : null}
        {route !== undefined ? (
          <RailFact>
            {route.siteName} · {route.routeName}
          </RailFact>
        ) : null}
        {days !== null ? <RailFact>{days}</RailFact> : null}
        {hours !== null ? <RailFact>{hours}</RailFact> : null}
        <RailFact>
          {headcount} {headcount === 1 ? "officer" : "officers"} per shift
        </RailFact>
      </ul>
      <p className="wizrail__note">
        Under the hood this saves as a template, so it can repeat any week. You never need
        that word to finish this flow.
      </p>
    </div>
  );
}

function CarryForwardRail({
  patternLabel,
  route,
  days,
  hours,
  headcount,
}: {
  patternLabel: string | null;
  route: ScheduleRouteOption | undefined;
  days: string | null;
  hours: string | null;
  headcount: number;
}) {
  return (
    <div className="wizrail">
      <p className="wizrail__label">Carrying forward</p>
      <ul className="wizrail__facts">
        {patternLabel !== null ? (
          <RailFact>
            {patternLabel}
            {route !== undefined ? ` · ${route.routeName}` : ""}
          </RailFact>
        ) : null}
        {days !== null || hours !== null ? (
          <RailFact>{[days, hours].filter((part) => part !== null).join(" · ")}</RailFact>
        ) : null}
        <RailFact>
          {headcount} {headcount === 1 ? "officer" : "officers"} per shift
        </RailFact>
      </ul>
    </div>
  );
}

function ReviewSideRail({
  preview,
  coverageReady,
  daysSummaryLabel,
}: {
  preview: PublishPreview;
  coverageReady: boolean;
  daysSummaryLabel: string | null;
}) {
  // Green chips are facts that already ran, never promises about the button (frame 04).
  const weekClear = coverageReady && preview.alreadyPublishedCount === 0 && preview.newSlotCount > 0;
  const patternComplete = daysSummaryLabel !== null;
  return (
    <div className="wizrail">
      <p className="wizrail__label">Checks that already ran</p>
      <ul className="wizrail__facts">
        {weekClear ? (
          <RailFact>
            <span className="wizrail__proof">Week is clear · nothing published for this pattern in the chosen week</span>
          </RailFact>
        ) : null}
        {patternComplete ? (
          <RailFact>
            <span className="wizrail__proof">Pattern complete · days, hours, and headcount all set</span>
          </RailFact>
        ) : null}
        {!weekClear && !patternComplete ? (
          <RailFact>No checks have passed yet. The reason line below names the first gap.</RailFact>
        ) : null}
      </ul>
      <p className="wizrail__label">After publish</p>
      <p className="wizrail__note">
        Shifts appear in Guard under My Shift, Open. Assigning, copying to more weeks, and
        week cancel live in Builder, one click from the success screen.
      </p>
    </div>
  );
}

function PublishedSideRail({ openFillMode, calendarMode }: { openFillMode: string; calendarMode: boolean }) {
  const destination = calendarMode ? "Calendar" : "Builder";
  return (
    <div className="wizrail">
      <p className="wizrail__label">What officers see now</p>
      <ul className="wizrail__facts">
        <RailFact>Visible in Guard under My Shift, Open, right now</RailFact>
        {openFillMode === "request_approval" ? (
          <RailFact>Requests route to Schedule, Requests for approval</RailFact>
        ) : (
          <RailFact>Officers can take these shifts in Guard as open shifts</RailFact>
        )}
        <RailFact>Assigning officers happens in {destination}, one click away</RailFact>
      </ul>
      <p className="wizrail__note">
        Undo is per slot or per week in {destination}, confirm-gated. A cancelled time still
        blocks republish of that same time.
      </p>
    </div>
  );
}
